// External libraries
import debounce from 'lodash.debounce';

// Internal modules
import {
  appStore,
  dispatchFormAction,
  getBaseAddressFields,
  getDefaultInputState,
} from '@/store';
import {
  browserAutocompleteCallback,
  CI,
  features,
  getAddressMetaKey,
  getLocale,
  logger,
  noop,
} from '@/lib';
import { getShippingAddress } from '@/lib/utils/shipping';

import {
  fetchAddressMetadata,
  getAddressFromAutocompletePlaceId,
  throttledGetAddressAutocomplete,
} from './api';
import { getFieldData } from '../Form/utils';
import { AUTO_COMPLETE_TYPE } from '@/store';
import { TRACK_ADDRESS_INPUT_PAYLOAD } from './fpti';
import {
  clearApiAddressValidationError,
  isShippingCallbackRequiredAddressValid,
  isStateAndPostalCodeValidForShipping,
  validateAddressFields,
} from './utils';
import { conditionallyCalculateShippingAndTaxes } from '../Shipping/utils';
import { SHIPPING_AND_CALLBACK_ADDRESS_FIELDS } from './constants';

const getShippingCountryChangeHandler =
  (props, onShippingCallback = noop) =>
  (...args) => {
    const [e, country] = args;
    if (props.disabled) {
      return e?.preventDefault();
    }

    fetchAddressMetadata(country).then(() => {
      const payload = {
        // reset the address fields
        ...getBaseAddressFields(),
        // set account quality meta data
        autoCompleteType: {
          value: AUTO_COMPLETE_TYPE.MANUAL,
        },
        isManipulated: {
          value: true,
        },
      };

      logger.info('shipping_country_changed_and_fields_cleared');
      appStore.getActions().form.updateAddressField({
        addressType: 'shippingAddress',
        isBatchUpdate: true,
        payload,
      });
    });

    props?.changeHandler(...args);
    conditionallyCalculateShippingAndTaxes();
    onShippingCallback();
  };

/**
 *
 * @param {Event} e onBlur
 * @param {Function} onShippingCallback - represents any of the shipping
 *   and callbacks(V1 & V2).
 * @NOTE `Snc` stands for `Shipping and callback`
 *
 * @returns {undefined}
 */
const getSncAddressFieldsBlurHandler = (e, onShippingCallback = noop) => {
  const { form: formState } = appStore.getState();
  const shippingAddress = getShippingAddress();

  const { shipToBilling } = formState;
  const fieldNameWithPrefix = e?.target?.name || '';
  const fieldNameWithoutPrefix = fieldNameWithPrefix.replace(
    /billing|shipping/g,
    '',
  );
  // Convert field name to camel case
  const fieldNameCamelCase =
    fieldNameWithoutPrefix[0].toLowerCase() + fieldNameWithoutPrefix.slice(1);

  // Do not invoke shipping callback if billing address is changed and
  // shipToBilling is unchecked.
  if (!shipToBilling.value && fieldNameWithPrefix.includes('billing')) {
    return;
  }

  // Do not invoke callback if:
  //  1. field isn't part of the shipping callback address fields. I.e `line1`
  //  2. OR field is empty.
  if (
    !SHIPPING_AND_CALLBACK_ADDRESS_FIELDS.has(
      fieldNameWithoutPrefix.toLowerCase(),
    ) ||
    !shippingAddress[fieldNameCamelCase]?.value?.length
  ) {
    return;
  }

  // The shipping callback should be invoked only when user has already filled
  // all of the shipping callback required fields.
  if (!isShippingCallbackRequiredAddressValid(shippingAddress)) {
    return;
  }

  onShippingCallback();
};

const getSntAddressFieldsBlurHandler = e => {
  const { form: formState } = appStore.getState();
  const { shipToBilling } = formState;
  const fieldName = e.target.name.toLowerCase();
  // Do not trigger API if billing address is changed and shipToBilling is
  // unchecked
  if (!shipToBilling.value && fieldName.includes('billing')) {
    return;
  }

  if (fieldName.includes('state') || fieldName.includes('postalcode')) {
    if (!isStateAndPostalCodeValidForShipping()) {
      logger.warn('calculate_shipping_and_taxes_invalid_fields');
      return;
    }
    conditionallyCalculateShippingAndTaxes();
  }
};

const shipToBillingChangeHandler = e => {
  // Ensure the value can't be updated if shipping country is restricted
  const { api: apiState, form: formState } = appStore.getState();
  const { country: merchantCountry } = apiState.cartDetails.merchant;
  const isNonDomesticTransaction = getLocale().country !== merchantCountry;
  const isShippingRestricted = features.isActive('blockNonDomesticShipping');
  const hasShippingAddressQualityExperiment = features.isActive(
    'hasShippingAddressQualityExperiment',
  );

  if (isShippingRestricted && isNonDomesticTransaction) {
    return;
  }

  const shipToBilling = e.target.checked;
  if (!shipToBilling) {
    CI.start(CI.Event.SAME_AS_SHIPPING_ADDRESS_RENDER);
  }

  const { billingAddress, firstName, lastName, shippingAddress } = formState;
  const { form: formActions } = appStore.getActions();

  // If the shipping and billing country are the same, copy over billing
  // field values to shipping values state that are currently empty
  const { country } = getLocale();
  if (!shipToBilling && shippingAddress.country.value === country) {
    const billingFields = {
      ...billingAddress,
      firstName,
      lastName,
    };

    const shippingPayload = Object.entries(billingFields).reduce(
      (accum, [fieldName, fieldState]) => {
        if (!shippingAddress[fieldName].value && fieldState.value) {
          const { displayError, error, errorCode, isDirty, isValid, value } =
            fieldState;

          // We are passing error related keys so that if a user unchecks
          // the checkbox after seeing a validation error on billing address,
          // then we would want these errors to show up on shipping as well
          // Eg: Address_validation_error -> submit the page and then uncheck
          // the shipToBilling checkbox
          accum[fieldName] = {
            displayError,
            error,
            errorCode,
            isDirty,
            isValid,
            value,
          };
        }

        return accum;
      },
      {},
    );

    if (
      Object.keys(shippingPayload)?.length &&
      !hasShippingAddressQualityExperiment
    ) {
      // meta has hasValidationError set which needs to be copied as well
      // If we dont then validation error will not be cleared if we edit
      // one of the invalid fields after address_validation_error from backend
      shippingPayload.meta = billingFields.meta || {};
      formActions.updateAddressField({
        addressType: 'shippingAddress',
        isBatchUpdate: true,
        payload: shippingPayload,
      });
    } else if (hasShippingAddressQualityExperiment) {
      // when address quality experiment is on
      // clear the shipping fields and prefill only
      // first and last name
      const shippingFields = Object.entries(billingFields).reduce(
        (accum, [fieldName]) => {
          if (fieldName !== 'firstName' && fieldName !== 'lastName') {
            accum[fieldName] = {
              displayError: false,
              error: '',
              errorCode: '',
              isDirty: false,
              isValid: false,
              value: '',
            };
          }
          return accum;
        },
        {},
      );
      const shippingPayload = {
        firstName,
        lastName,
        ...shippingFields,
      };
      formActions.updateAddressField({
        addressType: 'shippingAddress',
        isBatchUpdate: true,
        payload: shippingPayload,
      });
    }
  }

  if (
    shippingAddress?.country?.value &&
    shippingAddress.country.value !== getLocale().country
  ) {
    fetchAddressMetadata(shippingAddress.country.value);
  }

  formActions.updateShipToBilling(shipToBilling);
  conditionallyCalculateShippingAndTaxes();
};

const debounceDispatches = addressType => {
  return debounce(() => {
    const { form: formActions } = appStore.getActions();
    const payload = {
      autoCompleteType: {
        value: AUTO_COMPLETE_TYPE.BROWSER,
      },
      isManipulated: {
        value: false,
      },
    };

    formActions.updateAddressField({
      addressType,
      isBatchUpdate: true,
      payload,
    });
  }, 100);
};

const debounceShippingAddress = debounceDispatches('shippingAddress');
const debounceBillingAddress = debounceDispatches('billingAddress');

const getAddressFieldsChangeHandler =
  ({
    country,
    props,
    setShowAutoCompleteSingleLine,
    setSuggestedAddressList,
    suppressBrowserAutofill,
  }) =>
  (e, value, oldValue, meta) => {
    const { addressType: namespace, field } = getFieldData(e.target, meta);
    const { addressType } = meta;
    const inputLengthToTriggerApi = suppressBrowserAutofill ? 2 : 4;
    const charCount = value?.length;
    const fieldType = e.target.type;
    const hasAddressAutoCompleteSingleLine = features.isActive(
      'hasAddressAutoCompleteSingleLine',
    );

    if (
      field === 'line1' &&
      oldValue.length < inputLengthToTriggerApi &&
      charCount > inputLengthToTriggerApi &&
      hasAddressAutoCompleteSingleLine
    ) {
      // If user entered address thru browser autofill
      // show the rest of the address fields
      setShowAutoCompleteSingleLine(false);
      setSuggestedAddressList([]);
    } else if (field === 'line1' && charCount >= inputLengthToTriggerApi) {
      // If user clicks on browser autofill then do not trigger the API
      if (charCount <= oldValue.length + 1) {
        throttledGetAddressAutocomplete(
          { addressType, country, input: value },
          setSuggestedAddressList,
        );
      } else {
        logger.info(`${addressType}_line1_multiple_char_change_no_api_call`);
        setSuggestedAddressList([]);
      }
    } else if (field === 'line1' && charCount < inputLengthToTriggerApi) {
      setSuggestedAddressList([]);
    }

    // If user enters a character into line2 field then log the same
    // or log change in value which is more than one character entered
    // which basically means browser autofill, or copy paste or a plugin entry
    if (
      (field === 'line2' && charCount <= 1) ||
      charCount > oldValue.length + 1
    ) {
      logger.fpti(
        `${field}_value_change`,
        TRACK_ADDRESS_INPUT_PAYLOAD({
          addressType,
          charCount,
          fieldName: field,
          fieldType,
        }),
      );
    }

    /**
     * The address validation error is an error that is returned from the API
     * when there is a mismatch between the city, state, and postal code. Since
     * we don't know which field is incorrect, when the user changes one of
     * those fields we clear out the error on the other two fields.
     */
    clearApiAddressValidationError({ addressType: namespace, field });

    props.changeHandler(e, value, oldValue, meta);

    browserAutocompleteCallback(e, () => {
      if (addressType === 'shipping') {
        debounceShippingAddress();
      } else {
        debounceBillingAddress();
      }
    });
  };

const updateShippingOnAddressSelection = (addressType, onShippingCallback) => {
  const { form: formState } = appStore.getState();
  const { shipToBilling } = formState;
  if (
    addressType === 'shipping' ||
    (addressType === 'billing' && shipToBilling.value)
  ) {
    if (!isStateAndPostalCodeValidForShipping()) {
      logger.warn('calculate_shipping_and_taxes_invalid_fields');
      return;
    }
    logger.info('calculate_shipping_and_taxes_on_address_selection');
    conditionallyCalculateShippingAndTaxes();
    onShippingCallback();
  }
};

const getAddressSelectionHandler =
  ({
    onShippingCallback = noop,
    setShowAutoCompleteSingleLine,
    setSuggestedAddressList,
    suggestedAddressList,
  }) =>
  (e, { addressType, placeId }) => {
    const hasAddressAutoCompleteSingleLine = features.isActive(
      'hasAddressAutoCompleteSingleLine',
    );

    if (placeId === 'IDONTSEEIT' && hasAddressAutoCompleteSingleLine) {
      setShowAutoCompleteSingleLine(false);

      return;
    }
    if (!placeId) {
      logger.fpti('google_address_select_place_id_missing', {
        state_name: 'billing_page_google_address_autocomplete',
        transition_name: 'process_google_address_selection_place_id_missing',
      });
      setShowAutoCompleteSingleLine(false);

      return;
    }

    logger.fpti('google_address_select_attempt', {
      state_name: 'billing_page_google_address_autocomplete',
      suggestion_used: placeId,
      transition_name: 'process_google_address_selection',
    });
    e.preventDefault();
    const addressIndex = suggestedAddressList.findIndex(
      addr => addr.placeId === placeId,
    );

    setSuggestedAddressList([suggestedAddressList[addressIndex]]);

    const getFieldPayload = ({ value }) => {
      const isValid = Boolean(value);
      const payload = {
        ...getDefaultInputState(),
        isValid,
        value: value || '', // Handle null as value
      };

      return payload;
    };

    getAddressFromAutocompletePlaceId({
      addressIndex,
      addressType,
      placeId,
    }).then(result => {
      if (Object.keys(result || {}).length > 0) {
        const { form: formActions } = appStore.getActions();
        const { city, country, line1, postalCode, state } = result;
        const payload = {
          city: getFieldPayload({ field: 'city', value: city }),
          line1: getFieldPayload({ field: 'line1', value: line1 }),
          postalCode: getFieldPayload({
            field: 'postalCode',
            value: postalCode,
          }),
          state: getFieldPayload({
            field: 'state',
            value: state?.toUpperCase(),
          }),
        };

        // Validate address fields and then update the state so that
        // state maintains right validation values
        const { api: apiState } = appStore.getState();
        const addressMeta =
          apiState.griffinMetadata.address[getAddressMetaKey(country)];
        const validatedPayload = validateAddressFields({
          address: payload,
          addressMeta,
          shouldUpdateValue: true,
        });

        formActions.updateAllAddressFieldsUsingGoogleAutocomplete({
          addressType: `${addressType}Address`,
          isBatchUpdate: true,
          payload: validatedPayload,
        });

        // calculate shipping and taxes when the address is
        // populated with auto complete and only when there is
        // a change in shipping address
        updateShippingOnAddressSelection(addressType, onShippingCallback);
        setShowAutoCompleteSingleLine(false);

        // Focus line2 to make sure user verifies the address and enters
        // missing apartment number if needed
        const line2Element = document.getElementById(`${addressType}Line2`);

        line2Element?.focus();
      }
    });
  };

// Generic reset handler that can reset any field to default value
const resetHandler = (e, { addressType: type, id, name }) => {
  e.preventDefault();

  // type value is billing or shipping
  // addressType value is billingAddress or shippingAddress
  const { addressType, field } = getFieldData(
    { id, name },
    { addressType: type },
  );
  const payload = getDefaultInputState();

  dispatchFormAction({ addressType, field, payload });
};

// We are setting the UI element focus to line1 and then
// Reset address line1 input state to default state
const getResetHandler =
  ({ setSuggestedAddressList }) =>
  (e, meta) => {
    const element = document.getElementById(meta.id);

    // Focus on the input element which has reset button
    element?.focus();
    // Clear the suggested address list when reset
    // button is clicked by user
    setSuggestedAddressList([]);
    // Call default resetHandler to update field state
    resetHandler(e, meta);
  };

const shareAddressWithDonateeChangeHandler = e => {
  const shareAddressWithDonatee = e.target.checked;
  const { form: formActions } = appStore.getActions();

  formActions.updateShareAddressWithDonatee(shareAddressWithDonatee);

  logger.fpti('weasley_share_address_with_donatee_consent', {
    is_checked: shareAddressWithDonatee ? 1 : 0,
    state_name: 'billing',
    transition_name: 'process_doner_consent',
  });
};

export {
  getAddressFieldsChangeHandler,
  getAddressSelectionHandler,
  getResetHandler,
  getShippingCountryChangeHandler,
  getSncAddressFieldsBlurHandler,
  getSntAddressFieldsBlurHandler,
  shareAddressWithDonateeChangeHandler,
  shipToBillingChangeHandler,
};
